import type { TeacherGameSummary } from '@wx-weiqi/contracts';

function gameDate(game: TeacherGameSummary): string {
  return new Date(game.startedAt ?? game.updatedAt).toLocaleDateString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
}

export function reviewableTeacherGames(games: readonly TeacherGameSummary[]) {
  return games.filter((game) => game.status === 'FINISHED');
}

export type TeacherGamePickerScope = 'CLASS' | 'STUDENT';

const SCOPE_COPY: Readonly<
  Record<TeacherGamePickerScope, Readonly<{ eyebrow: string; empty: string; loading: string }>>
> = {
  CLASS: {
    eyebrow: '班级学生棋谱',
    empty: '当前班级暂无可查看的已结束棋谱。',
    loading: '正在读取班级棋谱…',
  },
  STUDENT: {
    eyebrow: '学生账号棋谱',
    empty: '该学生账号暂无可查看的已结束棋谱。',
    loading: '正在读取该学生棋谱…',
  },
};

interface TeacherGamePickerDialogProps {
  readonly games: readonly TeacherGameSummary[];
  readonly currentGameId: string | null;
  readonly title: string;
  readonly scope: TeacherGamePickerScope;
  readonly pending: boolean;
  readonly error: string | null;
  readonly onClose: () => void;
  readonly onRefresh: () => void;
  readonly onSelect: (gameId: string) => void;
  readonly onDelete?: (game: TeacherGameSummary) => void;
}

export function TeacherGamePickerDialog({
  games,
  currentGameId,
  title,
  scope,
  pending,
  error,
  onClose,
  onRefresh,
  onSelect,
  onDelete,
}: TeacherGamePickerDialogProps) {
  const visibleGames = reviewableTeacherGames(games);
  const copy = SCOPE_COPY[scope];
  return (
    <section
      className="fox-kifu-details-backdrop teacher-game-picker-backdrop"
      role="presentation"
      onMouseDown={(event) => {
        if (event.currentTarget === event.target && !pending) onClose();
      }}
    >
      <div
        className="fox-kifu-details-dialog teacher-game-picker-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="teacher-game-picker-title"
      >
        <header>
          <span>
            <small>{copy.eyebrow}</small>
            <h2 id="teacher-game-picker-title">{title}</h2>
          </span>
          <button type="button" disabled={pending} onClick={onClose}>
            关闭
          </button>
        </header>
        <div className="teacher-game-picker-content" aria-live="polite" aria-busy={pending}>
          <div
            className={`teacher-game-picker-heading${onDelete === undefined ? '' : ' has-delete'}`}
          >
            <span>对局日期</span>
            <span>黑方</span>
            <span>白方</span>
            <span>棋局</span>
            <span>操作</span>
          </div>
          {error !== null ? <p className="message message--error">{error}</p> : null}
          {visibleGames.length === 0 && !pending && error === null ? (
            <p className="history-empty">{copy.empty}</p>
          ) : null}
          <div className="teacher-game-picker-list">
            {visibleGames.map((game) => {
              const black = game.players.find((player) => player.seat === 'BLACK');
              const white = game.players.find((player) => player.seat === 'WHITE');
              const selected = game.gameId === currentGameId;
              return (
                <div className="teacher-game-picker-row" key={game.gameId}>
                  <button
                    className={`teacher-game-picker-select${selected ? ' is-selected' : ''}`}
                    type="button"
                    aria-current={selected ? 'true' : undefined}
                    disabled={pending}
                    onClick={() => {
                      if (selected) onClose();
                      else onSelect(game.gameId);
                    }}
                  >
                    <span data-label="对局日期">{gameDate(game)}</span>
                    <span data-label="黑方">{black?.displayName ?? '—'}</span>
                    <span data-label="白方">{white?.displayName ?? '—'}</span>
                    <span data-label="棋局">
                      {game.boardSize} 路 · {game.currentSequence} 手
                    </span>
                    <strong data-label="操作">{selected ? '当前棋谱' : '切换棋谱'}</strong>
                  </button>
                  {onDelete === undefined ? null : (
                    <button
                      className="teacher-game-picker-delete"
                      type="button"
                      disabled={pending || game.accessLevel === 'OBSERVE'}
                      title={
                        game.accessLevel === 'OBSERVE'
                          ? '只能删除具有班级管理权限的已结束对局棋谱'
                          : '删除棋谱'
                      }
                      aria-label={`删除${black?.displayName ?? '黑方'}与${white?.displayName ?? '白方'}的棋谱`}
                      onClick={() => {
                        if (
                          window.confirm(
                            `确认删除 ${gameDate(game)} ${black?.displayName ?? '黑方'} 对 ${white?.displayName ?? '白方'} 的棋谱？\n删除后将从师生棋谱列表移除，底层对局与审计记录保留。`,
                          )
                        )
                          onDelete(game);
                      }}
                    >
                      删除
                    </button>
                  )}
                </div>
              );
            })}
          </div>
          {pending ? <p className="teacher-game-picker-loading">{copy.loading}</p> : null}
        </div>
        <footer>
          <span>共 {visibleGames.length} 盘已结束对局</span>
          <div>
            <button type="button" disabled={pending} onClick={onRefresh}>
              {pending ? '正在刷新…' : '刷新列表'}
            </button>
            <button type="button" disabled={pending} onClick={onClose}>
              关闭
            </button>
          </div>
        </footer>
      </div>
    </section>
  );
}
